/** @format */

import React, { useState, useEffect, useMemo } from 'react'
import {
  _$,
  Icon,
  Row,
  Col,
  Select,
  OptionGroup,
  Option,
  FormItem,
  Radio,
  Checkbox
} from 'kpc-react'
import styled from 'styled-components'
import { cloneDeep } from 'lodash'
import { FormItemLabelToopltip } from 'src/components'
import { IMonitorGraphLineData } from './constants'
import SelectInstance from 'src/components/SelectInstance'
import TagsDialog from './TagsDialog'
import { MetricItem, ProductItem } from 'src/utils/interface'
import variable from 'src/static/styles/var'
import API from 'src/api/ApiDashboard'
import { request } from 'src/utils'
import ProductGroupSelect from 'src/components/ProductGroupSelect'
import { useAsync, useDeepCompareEffect, useAsyncFn } from 'react-use'

interface EditGraphMetricFormItemProps {
  className?: string
  productList: ProductItem[]
  index: number
  // instanceGroup: GroupItemProps[]
  metricItem: IMonitorGraphLineData
  memberList: IMonitorGraphLineData[]
  handleChangeMetrics: (value: IMonitorGraphLineData[]) => void
  handleLineCount: () => boolean
}

export const aggregator_menu = {
  max: _$('最大值'),
  min: _$('最小值'),
  average: _$('平均值'),
  sum: _$('求和')
}

const EditGraphMetricFormItem: React.FC<EditGraphMetricFormItemProps> = ({
  className,
  productList,
  index,
  metricItem,
  memberList,
  handleChangeMetrics,
  handleLineCount
}) => {
  const [formValues, setFormValues] = useState<IMonitorGraphLineData>(metricItem)

  const changvalues = (index, values) => {
    const cloneMetrics = cloneDeep(memberList)
    cloneMetrics[index] = { ...cloneMetrics[index], ...values }
    handleChangeMetrics(cloneMetrics)
    unmatch_fetch(cloneMetrics[index])
    // 修改form
    setFormValues({
      ...formValues,
      ...values
    })
  }

  // 获取产品线下的监控项
  const { value: monitorList, loading: monitorLoading } = useAsync(async () => {
    if (formValues.productType !== undefined && formValues.productType !== null) {
      const result = await request(API.DescribeDashboardMetrics, {
        method: 'post',
        data: {
          ProductType: formValues.productType
        }
      })
      if (result.code === 'Success') {
        const metricsGroup = []
        result.data?.metricList.forEach(
          ({ application, metricKey, metricName, units, delay, isDimension }) => {
            const key = application || _$('其它')
            const findIndex = metricsGroup.findIndex(({ app }) => app === key)
            if (findIndex >= 0) {
              metricsGroup[findIndex].children.push({
                metricKey,
                metricName,
                units,
                delay,
                isDimension
              })
            } else {
              metricsGroup.push({
                app: key,
                children: [{ metricKey, metricName, units, delay, isDimension }]
              })
            }
          }
        )
        return metricsGroup
      } else {
        return []
      }
    }
  }, [formValues.productType])

  // 维度列表
  const { value: tags } = useAsync(async () => {
    const { productType, metricKey, instances, isDimension, metricTags } = formValues
    if (
      productType !== undefined &&
      metricKey &&
      instances?.length &&
      (isDimension === 1 || metricTags?.length)
    ) {
      if (isDimension === 2) {
        changvalues(index, {
          metricTags: []
        })
        return []
      }
      const result = await request(API.getDashboardMetricTags, {
        method: 'post',
        data: {
          ProductType: productType,
          MetricKey: metricKey,
          Instances: instances.map(item => item.instanceId)
        }
      })
      if (result.code === 'Success') {
        return result.data.metricTags || []
      } else {
        return []
      }
    } else {
      return []
    }
  }, [formValues.productType, formValues.metricKey, formValues.instances, formValues.isDimension])

  useDeepCompareEffect(() => {
    const { productType, metricKey, instances } = formValues
    if (productType === undefined || !metricKey || !instances?.length) {
      return
    }
    if (!formValues.metricTags?.length) {
      if (!tags?.length) {
        changvalues(index, {
          metricTags: []
        })
      } else if (tags?.length === 1) {
        changvalues(index, {
          metricTags: tags
        })
      }
    } else {
      if (tags?.length) {
        const filter = formValues.metricTags.filter(item => tags.includes(item))
        changvalues(index, {
          metricTags: filter
        })
      }
    }
  }, [tags])

  // 验证资源跟维度是否匹配
  const [{ value: unmatch_instances, loading }, unmatch_fetch] = useAsyncFn(async params => {
    const { productType, metricKey, instances, metricTags, isDimension } = params
    if (
      productType !== undefined &&
      metricKey &&
      instances?.length &&
      metricTags?.length &&
      isDimension !== 2
    ) {
      const result = await request(API.getDashboardInstanceMetricTags, {
        method: 'post',
        data: {
          ProductType: productType,
          MetricKey: metricKey,
          Instances: instances.map(item => item.instanceId),
          MetricTags: metricTags
        }
      })
      return result?.data?.instances || []
    }
  }, [])

  // 复制metric
  const copyMetric = metric => {
    if (handleLineCount()) {
      const copy = cloneDeep(metric)
      const cloneMetrics = cloneDeep(memberList)
      copy.graphMetricId = Math.round(-1000 * Math.random())
      cloneMetrics.push(copy)
      handleChangeMetrics(cloneMetrics)
    }
  }

  // 删除metric
  const removeMetric = index => {
    if (memberList?.length) {
      const cloneMetrics = cloneDeep(memberList)
      cloneMetrics.splice(index, 1)
      handleChangeMetrics(cloneMetrics)
    }
  }

  // 移动metric
  const moveMetric = (index: number, direction: 'up' | 'down') => {
    const cloneMetrics = cloneDeep(memberList)
    const copyItem = cloneDeep(memberList[index])
    cloneMetrics.splice(index, 1)
    if (direction === 'up') {
      cloneMetrics.splice(index - 1, 0, copyItem)
    } else {
      cloneMetrics.splice(index + 1, 0, copyItem)
    }
    handleChangeMetrics(cloneMetrics)
  }

  return (
    <FormItem noStyle hideLabel className={className}>
      <FormItem
        b-label={
          <FormItemLabelToopltip
            label={_$('监控项')}
            tips="请选择产品类型和监控项，定义展示在图表中的数据"
          />
        }
        value={formValues.metricKey}
      >
        <Row gutter="16">
          <Col span="12">
            <FormItem
              hideLabel
              value={formValues.productType}
              rules={{
                required: true
              }}
            >
              <ProductGroupSelect
                value={formValues.productType}
                width={300}
                placeholder={_$('请选择产品类型')}
                productList={productList}
                onChange={value => {
                  if (formValues.productType !== value) {
                    changvalues(index, {
                      productType: value,
                      metricKey: '',
                      metricName: '',
                      units: '',
                      delay: 0,
                      instances: [],
                      metricTags: []
                    })
                  }
                }}
              />
            </FormItem>
          </Col>
          <Col span="12">
            <FormItem
              hideLabel
              value={formValues.metricKey}
              rules={{
                required: true
              }}
            >
              <Select
                width={300}
                card
                value={formValues.metricKey}
                filterable
                placeholder={_$('选择监控项')}
                onChange={value => {
                  if (value) {
                    let find
                    for (const item of monitorList) {
                      const { children } = item
                      find = children.find(({ metricKey, metricName }) => `${metricKey}` === value)
                      if (find) break
                    }
                    if (find) {
                      changvalues(index, {
                        metricKey: find?.metricKey,
                        metricName: find?.metricName,
                        units: find?.units || '',
                        delay: find?.delay,
                        isDimension: find.isDimension || 0
                      })
                    }
                  }
                }}
              >
                {monitorList?.length
                  ? monitorList.map(({ app, children }) => (
                      <OptionGroup label={app} key={app}>
                        {children.map(item => (
                          <Option key={`${item.metricKey}`} value={`${item.metricKey}`}>
                            {item.metricName}
                          </Option>
                        ))}
                      </OptionGroup>
                    ))
                  : null}
              </Select>
            </FormItem>
          </Col>
        </Row>
      </FormItem>
      {formValues?.productType !== undefined && formValues.productType !== null ? (
        <FormItem label={_$('资源')} value={formValues.instances || []} rules={{ required: true }}>
          <SelectInstance
            productType={formValues.productType + ''}
            allInstance={false}
            projectDimens={false}
            instanceGroup={false}
            isGroupFilter={true}
            isRegionFilter={true}
            defaultRadioValue={3}
            defaultInstance={formValues.instances || []}
            batchSearch={true}
            instanceMaxLength={1000}
            onChange={(redio, value) => {
              if (redio === 3) {
                changvalues(index, {
                  instances: value
                })
              }
            }}
          />
        </FormItem>
      ) : null}
      {tags?.length ? (
        <FormItem
          value={formValues.metricTags}
          rules={{ required: true }}
          b-label={
            <FormItemLabelToopltip
              label={_$('维度')}
              tips="请先选择实例，再选择维度，维度只有一个时，默认选择，维度存在多个时，可选择多个维度进行展示，不选时默认选择全部维度。"
            />
          }
        >
          <TagsDialog
            values={formValues.metricTags}
            tags={tags}
            max={20}
            unmatch_instances={unmatch_instances}
            onChange={values => {
              changvalues(index, {
                metricTags: values
              })
            }}
          />
        </FormItem>
      ) : null}
      <FormItem
        b-label={
          <FormItemLabelToopltip
            label={_$('周期聚合方式')}
            tips="选择标签，对每个周期内数据进行分组聚合展示。"
          />
        }
        className="aggregator-radio"
        value={formValues.aggregator || 'max'}
        rules={{ required: true }}
        messages={{
          required: _$('请选择周期聚合方式')
        }}
      >
        {Object.keys(aggregator_menu).map(key => {
          const name = aggregator_menu[key]
          return (
            <Radio
              key={key}
              name="aggregator"
              trueValue={key}
              value={formValues.aggregator || 'max'}
              onChange={value => {
                changvalues(index, {
                  aggregator: value
                })
              }}
            >
              {_$(name)}
            </Radio>
          )
        })}
      </FormItem>
      <FormItem
        b-label={
          <FormItemLabelToopltip
            label={_$('实例聚合方式')}
            tips="可选且最多只能选其中一项，选择后会将多个实例数据按选项方式进行聚合操作。"
          />
        }
        className="aggregator-radio"
        value={formValues.resourceAggregator}
        messages={{
          required: _$('请选择实例聚合方式')
        }}
      >
        {Object.keys(aggregator_menu).map(key => {
          const name = aggregator_menu[key]
          return (
            <Checkbox
              key={key}
              name="resourceAggregator"
              trueValue={key}
              value={formValues.resourceAggregator}
              onChange={value => {
                changvalues(index, {
                  resourceAggregator: value ? value : ''
                })
              }}
            >
              {name}
            </Checkbox>
          )
        })}
      </FormItem>
      <div className="icon-list">
        <Icon
          className="ion-ios-copy"
          size={18}
          onClick={() => {
            copyMetric(metricItem)
          }}
        />
        {memberList.length > 1 ? (
          <Icon
            className="ion-android-delete"
            size={18}
            onClick={() => {
              removeMetric(index)
            }}
          />
        ) : null}
        {memberList.length > 1 && index !== 0 ? (
          <Icon
            className="ion-arrow-up-a"
            size={18}
            onClick={() => {
              moveMetric(index, 'up')
            }}
          />
        ) : null}
        {memberList.length > 1 && index !== memberList.length - 1 ? (
          <Icon
            className="ion-arrow-down-a"
            size={18}
            onClick={() => {
              moveMetric(index, 'down')
            }}
          />
        ) : null}
      </div>
    </FormItem>
  )
}

export default styled(EditGraphMetricFormItem)`
  width: 100%;
  margin-bottom: 8px;
  padding-top: 24px;
  border-top: 1px solid #e5e5e5;
  position: relative;
  /* & > .k-form-item:first-child {
      margin-bottom: 8px;
    } */
  .k-form-item {
    .tips {
      margin: 0;
      color: #999;
    }
    & > .k-label {
      width: 120px;
    }
  }
  .icon-list {
    position: absolute;
    height: 30px;
    top: 8px;
    right: 16px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    .k-icon {
      margin-left: 8px;
      cursor: pointer;
      transition: all ease-in-out 0.2s;
      &:hover {
        color: ${variable.primaryColor};
      }
    }
  }

  .aggregator-radio {
    .k-radio {
      margin-right: 8px;
    }
    .k-checkbox {
      margin-right: 8px;
    }
  }
`
